/**
 * Time Attack Matching Engine
 *
 * Implements Puyo-style matching for Time Attack mode.
 * Stepwise cascade model: each processMatches() call resolves one chain step.
 */

import { BaseMatchingEngine, MatchResult } from '../core/BaseMatchingEngine'
import { Grid, MatchGroup } from '../Grid'
import { GameBalanceManager } from '../GameBalanceManager'

export class TimeAttackMatchingEngine extends BaseMatchingEngine {
  constructor(grid: Grid, balanceManager?: GameBalanceManager) {
    super(grid, balanceManager)
  }

  /**
   * Process a single cascade step.
   * - If matches exist: increment chain, score/clear, apply gravity, return hasMore
   * - If no matches: return hasMore=false
   */
  public processMatches(): MatchResult {
    const matches = this.grid.findMatches()

    if (matches.length === 0) {
      return {
        matches: [],
        clearedCancer: [],
        totalCellsCleared: 0,
        chainLevel: this.currentChainLevel,
        scoreGained: 0,
        hasMore: false,
      }
    }

    this.currentChainLevel += 1
    const chainResult = this.calculateChainScore(matches, this.currentChainLevel)

    for (const match of matches) {
      this.grid.markCellsForDeletion(match.cells)
    }

    this.grid.removeMarkedCells()
    this.grid.applyGravity()

    const totalCellsCleared = matches.reduce((sum, match) => sum + match.size, 0)

    // Check if more matches exist after gravity settles
    const moreMatchesPending = this.grid.findMatches().length > 0

    return {
      matches,
      clearedCancer: [],
      totalCellsCleared,
      chainLevel: this.currentChainLevel,
      scoreGained: chainResult.score,
      hasMore: moreMatchesPending,
    }
  }

  /**
   * Calculates score for matches at a chain level
   */
  protected calculateChainScore(
    matches: MatchGroup[],
    chainLevel: number
  ): { score: number } {
    let score = 0
    const multiplier = this.getChainMultiplier(chainLevel)

    for (const match of matches) {
      const baseScore = this.calculateMatchScore(match)
      score += Math.floor(baseScore * multiplier)
    }

    return { score }
  }

  private calculateMatchScore(match: MatchGroup): number {
    let baseScore = match.size * 10
    if (match.size >= 5) baseScore += (match.size - 4) * 20
    if (match.size >= 8) baseScore += (match.size - 7) * 40
    return baseScore
  }

  public hasMatches(): boolean {
    return this.grid.findMatches().length > 0
  }

  public getPotentialMatches(): MatchGroup[] {
    return this.grid.findMatches()
  }
}
